import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import React, { useEffect, useRef, useState } from "react";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import { useU2000QuickSearchListEffect } from "../../u2000Core/hooks/useU2000QuickSearchListEffect";
import { useU2000ExportExcelHandlers } from "../../u2000Core/hooks/handler/U2000UseExportExcelHandlers";
import { useU2000ExportExcelNotification } from "../../u2000Core/hooks/handler/U2000UseExportExcelNotification";
import { useNavigate } from "react-router-dom";
import { u3101GetEmployeeListColumns } from "../tools/U3101EmployeeListColumns";

import type { U2000TableHandlers } from "../../u2000Core/types/U2000TableHandlers";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextPageAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU3101Context } from "../U3101Context";
import { doDeleteData, doExportExcel, doSearch, luHandleAppiError, type U3101Employee } from "../U3101Service";
import { NS, U3101ROLES } from "../U3101";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import U3101EmployeeDeleteDlg from "./common/U3101EmployeeDeleteDlg";
import { U3101EmployeeListToolBar } from "./list/U3101EmployeeListToolBar";
import U3101HourBankBulkCloneDlg from "./list/U3101HourBankBulkCloneDlg";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { u2000BuildQuickSearchFormPath, u2000ShouldQuickSearchOpenForm } from "../../u2000Core/tools/U2000QuickSearchUtils";
import {
  U2000_CRITERIA_LOOKUP_U3101_FIND_EMPLOYEE,
  defaultItemToValue,
  registerCriteriaLookup,
  unregisterCriteriaLookup,
  type U2000CriteriaLookupConfig,
} from "../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry";
import { u2000FormatEmployeeLookupLabel } from "../../u2000Core/tools/U2000EmployeeDisplayHelpers";
import { useU3101FindEmployeeHandlers } from "../U3101UseFindEmployeeHandlers";
import type { U3101FindEmployeeItem } from "../U3101Service";

export function useU3101EmployeeListHandlerActions<T>(handlers: U2000TableHandlers<T>): U2000TableHandlers<T> {
  return handlers;
}

function u3101EmployeeLookupItemToDisplay(item: unknown, _config: U2000CriteriaLookupConfig, inactiveSuffix: string): string {
  return u2000FormatEmployeeLookupLabel(item as U3101FindEmployeeItem, inactiveSuffix, 'dash-separated');
}

function u3101EmployeeLookupItemToSelectedDisplay(
  item: unknown,
  _config: U2000CriteriaLookupConfig,
  inactiveSuffix: string,
): string {
  return u2000FormatEmployeeLookupLabel(item as U3101FindEmployeeItem, inactiveSuffix, 'code-only');
}

const U3101EmployeeList: React.FC = () => {
  // Navigation
  const navigate = useNavigate();

  // Translation
  const { t, i18n } = useTranslation();

  // Toast
  const toast = useToast();

  // Auth context for role checking
  const { hasRole } = useU2000AuthContext();

  // Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3101Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb, quickSearchData, setQuickSearchData } = useU2000LogicalUnitContext();
  const consumeQsSessionRef = useRef<() => { isFromQuickSearch: boolean; targetLogicalUnit: string } | null>(() => null);
  const findEmployeeHandlers = useU3101FindEmployeeHandlers();

  useEffect(() => {
    const inactiveEmployeeSuffix = t('valNotActive', { ns: NS });
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3101_FIND_EMPLOYEE, {
      search: findEmployeeHandlers.search,
      itemToValue: defaultItemToValue,
      itemToDisplay: (item, config) => u3101EmployeeLookupItemToDisplay(item, config, inactiveEmployeeSuffix),
      itemToSelectedDisplay: (item, config) =>
        u3101EmployeeLookupItemToSelectedDisplay(item, config, inactiveEmployeeSuffix),
    });
    return () => unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3101_FIND_EMPLOYEE);
  }, [findEmployeeHandlers.search, t]);

  // Initialize columns on mount or when 't' changes
  useEffect(() => {
    searchStateHandlers.setColumns(u3101GetEmployeeListColumns(t));
    gridStateHandlers.setColumns(u3101GetEmployeeListColumns(t));
    setLogicalUnitBreadcrumb([{ label: t("title", { ns: NS }) }]);
  }, [t]);

  // Manage Handler Actions
  const u3101EmployeeListHandlerActions: U2000TableHandlers<U3101Employee> = useU3101EmployeeListHandlerActions<U3101Employee>({
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (employee: U3101Employee) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom("");
      const currentIdx = gridStateHandlers ? gridStateHandlers.getData().findIndex((r) => r.id === employee.id) : -1;
      if (gridStateHandlers) {
        gridStateHandlers.setCurrentIdx(currentIdx);
      }
      navigate(`${employee.id}`);
    },
    onAdd: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom("/U3101");
      navigate("new");
    },
    onModify: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom("/U3101");
      navigate(`${gridStateHandlers.getSelectedRows()[0]?.id}`);
    },
    onDelete: () => {
      handleDelete.handleDelete();
    },
    onExportExcel: async () => {
      const filter = searchStateHandlers.extractSearchParameter();
      await handleExportExcel.handleExportExcel(filter);
    },
  });

  // Call Search Handlers for Logical Unit
  const [bulkCloneDlgOpen, setBulkCloneDlgOpen] = useState(false);
  const handleSearch = useU2000SearchHandlers<U3101Employee>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      return await doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3101Employee>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());

      const qsSession = consumeQsSessionRef.current();
      const isFromQuickSearch = qsSession?.isFromQuickSearch === true;
      const targetLU = qsSession?.targetLogicalUnit ?? "/U3101";
      if (
        u2000ShouldQuickSearchOpenForm(window.location.pathname, targetLU) &&
        searchResult.data &&
        searchResult.data.length === 1 &&
        isFromQuickSearch
      ) {
        const singleEmployee = searchResult.data[0];
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        luStateHandlers.setActionOriginFrom("");
        const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === singleEmployee.id);
        if (currentIdx !== -1) {
          gridStateHandlers.setCurrentIdx(currentIdx);
        }
        navigate(u2000BuildQuickSearchFormPath(targetLU, singleEmployee.id), { replace: true });
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Export Excel Handlers for Logical Unit
  const handleExportExcel = useU2000ExportExcelHandlers({
    doExportExcelFn: async (filter, lang) => {
      return await doExportExcel(u2000AxiosAuthenticated, filter, lang);
    },
    i18nLanguage: i18n.language,
    onSuccess: (processId: string) => {
      toast.success(t("msgExportExcelStarted", { processId, ns: "common" }) || `Export en cours (référence: ${processId})`, t("successTitle", { ns: "common" }));
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Écouter les notifications WebSocket pour les exports Excel terminés
  useU2000ExportExcelNotification();

  const { consumeQuickSearchSession } = useU2000QuickSearchListEffect({
    targetLogicalUnit: "/U3101",
    t,
    searchStateHandlers,
    gridStateHandlers,
    runSearch: handleSearch.handleSearch,
    quickSearchData,
    setQuickSearchData,
  });
  consumeQsSessionRef.current = consumeQuickSearchSession;

  // Gestion des Delete Record avec confirmation
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => (gridStateHandlers ? gridStateHandlers.getSelectedRows() : []),
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, row);
    },

    onSuccess: (deletedRows) => {
      const page = gridStateHandlers.getCurrentPage();
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row["codeEmployee"], ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      // Après suppression, aller au prochain enregistrement, aller vers l'avant si possible, sinon vers l'arrière, si aucun, retourner à la liste
      const action: U2000GridStateNextPageAfterDeleteReturn = gridStateHandlers.getNextPageAfterDelete(page, totalRows);
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // s'assurer que les colonnes sont chargées avant le rendu car creer des problemes d'affichage avec les columnes par defaut si searchStateHandlers.columns est vide au rendu
  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  // Render
  return (
    <>
      <U3101EmployeeDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U3101HourBankBulkCloneDlg
        isOpen={bulkCloneDlgOpen}
        onClose={() => setBulkCloneDlgOpen(false)}
      />
      <U2000BasicTable
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        actionsButtons={
          <U3101EmployeeListToolBar
            gridStateHandlers={gridStateHandlers as any}
            handlerActions={u3101EmployeeListHandlerActions as any}
            onPrepareHourBanksNextYear={() => setBulkCloneDlgOpen(true)}
          />
        }
        handlerActions={u3101EmployeeListHandlerActions}
        label={t("lblEmployee", {
          count: gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
        allowRowSelection={true}
        exportExcelTitle={t("btnExportExcel", { ns: "common" })}
        isExporting={handleExportExcel.isExporting}
        hasExportRole={hasRole(U3101ROLES.EXPORT)}
      />
    </>
  );
};

export default U3101EmployeeList;
